import { useState, useEffect } from "react"
import { Text, Box, HStack, Input, Badge, VStack, Button, Checkbox } from "@chakra-ui/react"
import { type ColumnDef } from "@tanstack/react-table"
import { useNavigate, useSearchParams } from "react-router-dom"
import SectionCard from "../../framework/SectionCard"
import DataTable from "../../framework/DataTable"
import { formatDateTime } from "../../utils/datetime"
import { useUsers } from "../../hooks/useUsers"
import ErrorAlert from "../../components/ErrorAlert"

type UserRow = {
  email: string
  id: string
  name: string | null
  createdAt: string
  lastLoginAt: string
  hasActiveSubscription: boolean
  usedPromoCode: boolean
  idpName: string | null
  idProvider: string
  platform: string | null
  model: string | null
  version: string | null
  installId: string | null
}

const columns: ColumnDef<UserRow>[] = [
  {
    accessorKey: "createdAt",
    header: "Created",
    id: "createdAt",
    cell: ({ getValue }) => formatDateTime(getValue<string>()),
    enableSorting: true,
  },
  {
    accessorKey: "email",
    header: "Email",
    id: "email",
    enableSorting: true,
  },
  {
    accessorKey: "name",
    header: "Profile name",
    id: "name",
    cell: ({ getValue }) => getValue() ?? "—",
    enableSorting: true,
  },
  {
    accessorKey: "idpName",
    header: "IDP name",
    id: "idpName",
    cell: ({ getValue }) => getValue() ?? "—",
    enableSorting: false,
  },
  {
    accessorKey: "idProvider",
    header: "Provider",
    id: "idProvider",
    cell: ({ getValue }) => {
      const provider = getValue<string>()
      return <Badge colorPalette="blue">{provider}</Badge>
    },
    enableSorting: false,
  },
  {
    accessorKey: "platform",
    header: "Platform",
    id: "platform",
    cell: ({ getValue }) => getValue<string>() ?? "—",
    enableSorting: false,
  },
  {
    accessorKey: "model",
    header: "Device",
    id: "model",
    cell: ({ getValue }) => getValue<string>() ?? "—",
    enableSorting: false,
  },
  {
    accessorKey: "version",
    header: "App Version",
    id: "version",
    cell: ({ getValue }) => getValue<string>() ?? "—",
    enableSorting: false,
  },
  {
    accessorKey: "hasActiveSubscription",
    header: "Subscribed",
    id: "hasActiveSubscription",
    cell: ({ row }) => {
      const hasSubscription = row.original.hasActiveSubscription
      const usedPromo = row.original.usedPromoCode
      if (!hasSubscription) return ""
      if (usedPromo) return <Badge colorPalette="purple">Promo</Badge>
      return <Badge colorPalette="green">Paid</Badge>
    },
    enableSorting: true,
  },
]

export default function UserList() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Initialize state from URL params
  const [globalFilter, setGlobalFilter] = useState(searchParams.get("filter") || "")
  const [sorting, setSorting] = useState([{ 
    id: searchParams.get("sort") || "createdAt", 
    desc: searchParams.get("dir") !== "asc" 
  }])
  const [offset, setOffset] = useState(parseInt(searchParams.get("offset") || "0"))
  
  // Initialize filter states with proper defaults
  const initialPaidOnly = searchParams.get("paidOnly") !== "false"
  const initialPromoOnly = searchParams.get("promoOnly") === "true"
  const initialIncludeAnonymous = (initialPaidOnly || initialPromoOnly) 
    ? false 
    : searchParams.get("includeAnonymous") !== "false"
  
  const [includeAnonymous, setIncludeAnonymous] = useState(initialIncludeAnonymous)
  const [paidOnly, setPaidOnly] = useState(initialPaidOnly)
  const [promoOnly, setPromoOnly] = useState(initialPromoOnly)
  const [includeTeam, setIncludeTeam] = useState(searchParams.get("includeTeam") === "true")

  const currentSort = sorting[0]
  const limit = 100

  // Update URL when state changes
  useEffect(() => {
    const params = new URLSearchParams()
    if (globalFilter) params.set("filter", globalFilter)
    params.set("sort", currentSort.id)
    params.set("dir", currentSort.desc ? "desc" : "asc")
    if (offset > 0) params.set("offset", offset.toString())
    if (!includeAnonymous) params.set("includeAnonymous", "false")
    if (paidOnly) params.set("paidOnly", "true")
    if (promoOnly) params.set("promoOnly", "true")
    if (includeTeam) params.set("includeTeam", "true")
    
    setSearchParams(params, { replace: true })
  }, [globalFilter, currentSort.id, currentSort.desc, offset, includeAnonymous, paidOnly, promoOnly, includeTeam, setSearchParams])

  // Determine actual filter values based on exclusive logic
  const actualIncludeAnonymous = (paidOnly || promoOnly) ? false : includeAnonymous
  const actualIncludePaid = paidOnly
  const actualIncludePromo = promoOnly

  const { data, isLoading, error } = useUsers({
    filter: globalFilter || undefined,
    sort: currentSort.id,
    dir: currentSort.desc ? "desc" : "asc",
    limit,
    offset,
    includeAnonymous: actualIncludeAnonymous,
    includePaid: actualIncludePaid,
    includePromo: actualIncludePromo,
    includeTeam,
  })

  const users = data?.users ?? []
  const total = data?.total ?? 0
  const hasMore = data?.hasMore ?? false

  const errorMessage = error ? (error as Error)?.message ?? "Unable to load users" : null

  const handlePrevPage = () => {
    setOffset(Math.max(0, offset - limit))
  }

  const handleNextPage = () => {
    setOffset(offset + limit)
  }

  const handleFilterChange = () => {
    // Reset to first page when filters change
    setOffset(0)
  }

  const handlePaidOnlyChange = (checked: boolean) => {
    setPaidOnly(checked)
    if (checked) {
      setPromoOnly(false)
      setIncludeAnonymous(false)
    }
    handleFilterChange()
  }

  const handlePromoOnlyChange = (checked: boolean) => {
    setPromoOnly(checked)
    if (checked) {
      setPaidOnly(false)
      setIncludeAnonymous(false)
    }
    handleFilterChange()
  }

  return (
    <SectionCard>
      <Text fontSize="md" mb="3">
        Registered users
      </Text>
      <VStack gap="3" mb="4" align="stretch">
        <HStack gap="3" flexWrap="wrap">
          <Input
            placeholder="Filter users"
            bg="gray.700"
            value={globalFilter}
            onChange={(event) => {
              setGlobalFilter(event.target.value)
              handleFilterChange()
            }}
            flex="1"
            minW="220px"
          />
          <Text fontSize="sm" color="gray.400" whiteSpace="nowrap">
            Showing {offset + 1}-{Math.min(offset + users.length, total)} of {total} users
          </Text>
        </HStack>
        <HStack gap="4" flexWrap="wrap">
          <Checkbox.Root
            checked={includeAnonymous}
            disabled={paidOnly || promoOnly}
            onCheckedChange={(e) => {
              setIncludeAnonymous(!!e.checked)
              handleFilterChange()
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>Include anonymous</Checkbox.Label>
          </Checkbox.Root>
          <Checkbox.Root
            checked={paidOnly}
            onCheckedChange={(e) => handlePaidOnlyChange(!!e.checked)}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>Paid subs only</Checkbox.Label>
          </Checkbox.Root>
          <Checkbox.Root
            checked={promoOnly}
            onCheckedChange={(e) => handlePromoOnlyChange(!!e.checked)}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>Promo subs only</Checkbox.Label>
          </Checkbox.Root>
          <Checkbox.Root
            checked={includeTeam}
            onCheckedChange={(e) => {
              setIncludeTeam(!!e.checked)
              handleFilterChange()
            }}
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>Include team devices</Checkbox.Label>
          </Checkbox.Root>
        </HStack>
      </VStack>
      {errorMessage && <ErrorAlert message={errorMessage} />}
      <Box overflowX="auto">
        <DataTable
          columns={columns}
          data={users}
          isLoading={isLoading}
          emptyMessage="No users yet."
          sorting={sorting}
          onSortingChange={(newSorting) => {
            setSorting(newSorting)
            handleFilterChange()
          }}
          globalFilter={globalFilter}
          manualSorting={true}
          onRowClick={(row) => navigate(`/users/${row.id}`)}
        />
      </Box>
      <HStack gap="3" mt="4" justify="space-between">
        <Button
          onClick={handlePrevPage}
          disabled={offset === 0}
          size="sm"
        >
          Previous
        </Button>
        <Text fontSize="sm" color="gray.400">
          Page {Math.floor(offset / limit) + 1} of {Math.ceil(total / limit)}
        </Text>
        <Button
          onClick={handleNextPage}
          disabled={!hasMore}
          size="sm"
        >
          Next
        </Button>
      </HStack>
    </SectionCard>
  )
}
